import React, { useState } from 'react';
import Header from './header';
import Footer from './footer';
import BannerRequestForm from './bannerRequestForm';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Form, ProgressBar } from 'react-bootstrap';
import axios from 'axios';

const BASE_URL = "https://admin.cappadociavisitor.com";

const TOURISM_VOLUME_OPTIONS = [
  { value: '0-100', label: '0-100 tourists/month' },
  { value: '101-500', label: '101-500 tourists/month' },
  { value: '501-1000', label: '501-1000 tourists/month' },
  { value: '1001-5000', label: '1001-5000 tourists/month' },
  { value: '5000+', label: 'More than 5000 tourists/month' }
];

const Partnership = () => {
  const { t } = useTranslation();
  
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companyName: '',
    businessType: '',
    website: '',
    contactName: '',
    contactSurname: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    monthlyTourismVolume: '',
    additionalInfo: ''
  });

  // Form validation
  const [errors, setErrors] = useState({});
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.companyName.trim()) newErrors.companyName = t('partnership.required');
      if (!formData.businessType.trim()) newErrors.businessType = t('partnership.required');
    }
    
    if (step === 2) {
      if (!formData.contactName.trim()) newErrors.contactName = t('partnership.required');
      if (!formData.contactSurname.trim()) newErrors.contactSurname = t('partnership.required');
      if (!formData.email.trim()) newErrors.email = t('partnership.required');
      if (!formData.phone.trim()) newErrors.phone = t('partnership.required');
      
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = t('partnership.invalidEmail');
      }
      
      if (formData.phone && !/^\+?[0-9\s\-()]{8,20}$/.test(formData.phone)) {
        newErrors.phone = t('partnership.invalidPhone');
      }
    }
    
    if (step === 3) {
      if (!formData.country.trim()) newErrors.country = t('partnership.required');
      if (!formData.city.trim()) newErrors.city = t('partnership.required');
      if (!formData.monthlyTourismVolume) newErrors.monthlyTourismVolume = t('partnership.required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BASE_URL}/api/v1/partnership`, formData);
      
      if (response.status === 200 || response.status === 201) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          businessType: '',
          website: '',
          contactName: '',
          contactSurname: '',
          email: '',
          phone: '',
          country: '',
          city: '',
          monthlyTourismVolume: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-step">
            <h3>{t('partnership.companyDetails')}</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.companyName')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    isInvalid={!!errors.companyName}
                    placeholder={t('partnership.placeholder.companyName')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.companyName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.businessType')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    isInvalid={!!errors.businessType}
                    placeholder={t('partnership.placeholder.businessType')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.businessType}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.website')}</Form.Label>
                  <Form.Control
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder={t('partnership.placeholder.website')}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        );
      
      case 2:
        return (
          <div className="form-step">
            <h3>{t('partnership.contactDetails')}</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.contactName')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    isInvalid={!!errors.contactName}
                    placeholder={t('partnership.placeholder.contactName')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.contactSurname')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactSurname"
                    value={formData.contactSurname}
                    onChange={handleChange}
                    isInvalid={!!errors.contactSurname}
                    placeholder={t('partnership.placeholder.contactSurname')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactSurname}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.email')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    placeholder={t('partnership.placeholder.email')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.phone')}</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    isInvalid={!!errors.phone}
                    placeholder={t('partnership.placeholder.phone')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>
        );
      
      case 3:
        return (
          <div className="form-step">
            <h3>{t('partnership.businessDetails')}</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.country')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    isInvalid={!!errors.country}
                    placeholder={t('partnership.placeholder.country')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.country}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.city')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    isInvalid={!!errors.city}
                    placeholder={t('partnership.placeholder.city')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.monthlyTourismVolume')}</Form.Label>
                  <Form.Select
                    name="monthlyTourismVolume"
                    value={formData.monthlyTourismVolume}
                    onChange={handleChange}
                    isInvalid={!!errors.monthlyTourismVolume}
                  >
                    <option value="">{t('partnership.placeholder.monthlyTourismVolume')}</option>
                    {TOURISM_VOLUME_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.monthlyTourismVolume}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label>{t('partnership.additionalInfo')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    placeholder={t('partnership.placeholder.additionalInfo')}
                    style={{ height: '120px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid w-100 h-100 p-0">
      <Header />
      <BannerRequestForm />
      
      <div className="partnership-form-background py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="partnership-form-container">
                <div className="form-content">
                  <div className="form-header text-center">
                    <h2>{t('partnership.title')}</h2>
                    <p>{t('partnership.subtitle')}</p>
                  </div>

                  <div className="wizard-progress mb-5">
                    <ProgressBar now={(currentStep / 3) * 100} />
                    <div className="step-indicators">
                      <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                        <div className="step-number">1</div>
                        <div className="step-title">{t('partnership.companyDetails')}</div>
                      </div>
                      <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                        <div className="step-number">2</div>
                        <div className="step-title">{t('partnership.contactDetails')}</div>
                      </div>
                      <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                        <div className="step-number">3</div>
                        <div className="step-title">{t('partnership.businessDetails')}</div>
                      </div>
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="success-message">
                      <div className="success-icon">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <p>{t('partnership.successMessage')}</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="error-message">
                      <div className="error-icon">
                        <i className="fas fa-exclamation-circle"></i>
                      </div>
                      <p>{t('partnership.errorMessage')}</p>
                    </div>
                  )}

                  <Form onSubmit={handleSubmit} className="partnership-wizard-form">
                    {renderStep()}

                    <div className="form-navigation">
                      {currentStep > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handlePrevious}
                        >
                          {t('previous')}
                        </button>
                      )}
                      
                      {currentStep < 3 ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleNext}
                        >
                          {t('next')}
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {t('partnership.submitting')}
                            </>
                          ) : (
                            t('partnership.submit')
                          )}
                        </button>
                      )}
                    </div>
                  </Form>
                </div>

                <div className="partnership-benefits">
                  <h3>{t('partnership.benefits.title')}</h3>
                  <ul className="benefits-list">
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span>{t('partnership.benefits.benefit1')}</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span>{t('partnership.benefits.benefit2')}</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span>{t('partnership.benefits.benefit3')}</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span>{t('partnership.benefits.benefit4')}</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span>{t('partnership.benefits.benefit5')}</span>
                    </li>
                  </ul>

                  <div className="contact-info">
                    <h4>{t('partnership.needHelp')}</h4>
                    <div className="contact-item">
                      <i className="fas fa-envelope"></i>
                      <span>info@cappadociavisitor.com</span>
                    </div>
                    <div className="contact-item">
                      <i className="fas fa-phone"></i>
                      <span>+90 530 652 47 68</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style jsx="true">{`
        .partnership-form-background {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          padding: 60px 0;
        }

        .partnership-form-container {
          display: flex;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-top: -30px;
          position: relative;
          z-index: 10;
        }

        .form-content {
          flex: 2;
          padding: 3.5rem;
          position: relative;
        }

        .partnership-benefits {
          flex: 1;
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          padding: 3.5rem 2rem;
          display: flex;
          flex-direction: column;
        }

        .form-header {
          margin-bottom: 3rem;
        }

        .form-header h2 {
          font-size: 2.5rem;
          color: #1e3c72;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .form-header p {
          color: #666;
          font-size: 1.1rem;
        }

        .wizard-progress {
          position: relative;
          margin-bottom: 3rem;
        }

        .step-indicators {
          display: flex;
          justify-content: space-between;
          margin-top: 1rem;
        }

        .step {
          text-align: center;
          flex: 1;
          position: relative;
        }

        .step-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e9ecef;
          color: #6c757d;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .step.active .step-number {
          background: #1e3c72;
          color: white;
        }

        .step-title {
          font-size: 0.9rem;
          color: #6c757d;
          margin-top: 0.5rem;
        }

        .step.active .step-title {
          color: #1e3c72;
          font-weight: 600;
        }

        .form-step {
          animation: fadeIn 0.5s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .form-navigation {
          display: flex;
          justify-content: space-between;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid #eee;
        }

        .btn {
          padding: 0.8rem 2rem;
          font-weight: 600;
          border-radius: 50px;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: #1e3c72;
          border-color: #1e3c72;
        }

        .btn-primary:hover {
          background: #2a5298;
          border-color: #2a5298;
          transform: translateY(-2px);
        }

        .btn-outline-primary {
          color: #1e3c72;
          border-color: #1e3c72;
        }

        .btn-outline-primary:hover {
          background: #1e3c72;
          color: white;
          transform: translateY(-2px);
        }

        .form-control {
          padding: 0.8rem 1.2rem;
          border-radius: 10px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .form-control:focus {
          border-color: #1e3c72;
          box-shadow: 0 0 0 0.2rem rgba(30, 60, 114, 0.15);
        }

        .benefits-list {
          list-style: none;
          padding: 0;
          margin: 2rem 0;
        }

        .benefits-list li {
          display: flex;
          align-items: center;
          margin-bottom: 1.2rem;
          font-size: 1.1rem;
        }

        .benefits-list li i {
          color: #4CAF50;
          margin-right: 1rem;
          font-size: 1.2rem;
        }

        .contact-info {
          margin-top: auto;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .contact-info h4 {
          font-size: 1.2rem;
          margin-bottom: 1rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
        }

        .contact-item i {
          margin-right: 1rem;
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
        }

        .success-message,
        .error-message {
          text-align: center;
          padding: 1.5rem;
          border-radius: 10px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .success-message {
          background-color: rgba(25, 135, 84, 0.1);
          color: #198754;
        }

        .error-message {
          background-color: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .success-icon,
        .error-icon {
          font-size: 1.5rem;
          margin-right: 1rem;
        }

        @media (max-width: 991.98px) {
          .partnership-form-container {
            flex-direction: column;
          }

          .form-content,
          .partnership-benefits {
            padding: 2rem;
          }

          .form-header h2 {
            font-size: 2rem;
          }

          .step-title {
            display: none;
          }
        }

        @media (max-width: 767.98px) {
          .partnership-form-background {
            padding: 20px 0;
          }

          .form-content {
            padding: 1.5rem;
          }

          .form-header h2 {
            font-size: 1.8rem;
          }

          .btn {
            padding: 0.6rem 1.5rem;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default Partnership; 